import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Play, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL, API } from '@/config';

const Gallery = () => {
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const { t } = useTranslation();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLanguage, setSelectedLanguage] = useState('all');

  useEffect(() => {
    fetchVideos();
  }, [selectedCategory, selectedLanguage]);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const qs = new URLSearchParams();
      if (selectedCategory !== 'all') qs.set('category', selectedCategory);
      if (selectedLanguage !== 'all') qs.set('language', selectedLanguage);
      const params = qs.toString() ? `?${qs.toString()}` : '';
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/videos${params}`, { headers });
      setVideos(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Error al cargar videos');
      setLoading(false);
    }
  };

  const handleVideoClick = (video) => {
    navigate(`/video/${video.id}`);
  };

  const categories = [
    { value: 'all', label: t('categories.all') },
    { value: 'nasca', label: t('categories.nasca') },
    { value: 'palpa', label: t('categories.palpa') },
    { value: 'museum', label: t('categories.museum') },
    { value: 'archaeological', label: t('categories.archaeological') },
    { value: 'nature', label: t('categories.nature') },
    { value: 'other', label: t('categories.other') }
  ];

  const languages = [
    { value: 'all', label: t('gallery.allLanguages'), flag: '🌐' },
    { value: 'es', label: t('languages.es'), flag: '🇪🇸' },
    { value: 'en', label: t('languages.en'), flag: '🇺🇸' },
    { value: 'ja', label: t('languages.ja'), flag: '🇯🇵' },
    { value: 'pt', label: t('languages.pt'), flag: '🇵🇹' },
    { value: 'zh', label: t('languages.zh'), flag: '🇨🇳' },
    { value: 'it', label: t('languages.it'), flag: '🇮🇹' },
    { value: 'other', label: t('languages.other') }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      <Navbar />

      <div className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div data-testid="gallery-header" className="text-center mb-12">
            <h1 className="text-5xl font-bold text-amber-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t('gallery.title')}
            </h1>
            <p className="text-xl text-gray-700">
              {t('gallery.subtitle')}
            </p>
          </div>

          {/* Category Filters */}
          <div data-testid="category-filters" className="flex flex-wrap justify-center gap-4 mb-6">
            {categories.map((category) => (
              <Button
                key={category.value}
                data-testid={`category-${category.value}`}
                onClick={() => setSelectedCategory(category.value)}
                className={`px-6 py-3 rounded-full font-medium ${
                  selectedCategory === category.value
                    ? 'bg-gradient-to-r from-amber-600 to-orange-700 text-white'
                    : 'bg-white text-gray-700 border-2 border-amber-300 hover:border-amber-500'
                }`}
              >
                {category.label}
              </Button>
            ))}
          </div>

          {/* Language Filters */}
          <div data-testid="language-filters" className="flex flex-wrap justify-center gap-2 mb-12">
            {languages.map((lang) => (
              <button
                key={lang.value}
                data-testid={`language-${lang.value}`}
                onClick={() => setSelectedLanguage(lang.value)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedLanguage === lang.value
                    ? 'bg-amber-900 text-white'
                    : 'bg-white text-amber-800 border border-amber-200 hover:bg-amber-50'
                }`}
              >
                {lang.flag && <span className="mr-1">{lang.flag}</span>}
                {lang.label}
              </button>
            ))}
          </div>

          {/* Videos Grid */}
          {loading ? (
            <div data-testid="loading-spinner" className="flex justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : (
            <div data-testid="videos-grid" className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {videos.map((video) => (
                <div
                  key={video.id}
                  data-testid={`video-card-${video.id}`}
                  className="card-peru cursor-pointer group"
                  onClick={() => handleVideoClick(video)}
                >
                  {/* Thumbnail */}
                  <div className="relative overflow-hidden">
                    {video.thumbnail_url ? (
                      <img
                        src={video.thumbnail_url.startsWith('/api') ? `${BACKEND_URL}${video.thumbnail_url}` : video.thumbnail_url}
                        alt={video.title}
                        className="w-full h-56 object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-56 bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                        <Play className="w-16 h-16 text-amber-500" />
                      </div>
                    )}

                    {/* Play Overlay */}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                        <Play className="w-8 h-8 text-amber-900 ml-1" />
                      </div>
                    </div>

                    {/* Duration */}
                    <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {video.duration}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="text-xl font-bold text-amber-900">
                        {video.title}
                      </h3>
                      {video.language && (
                        <span
                          data-testid={`video-lang-${video.id}`}
                          className="shrink-0 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide"
                          title={`Idioma: ${video.language}`}
                        >
                          {{ es: '🇪🇸 ES', en: '🇺🇸 EN', ja: '🇯🇵 JA', pt: '🇵🇹 PT', zh: '🇨🇳 ZH', it: '🇮🇹 IT' }[video.language] || video.language.toUpperCase()}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {video.description}
                    </p>
                    
                    {/* Tags */}
                    <div className="flex flex-wrap gap-2">
                      {video.cultural_tags?.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && videos.length === 0 && (
            <div data-testid="no-videos" className="text-center py-20">
              <p className="text-xl text-gray-600">No se encontraron videos en esta categoría</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Gallery;